// TanStack Query hooks against the Worker API. Single source of truth for query keys.
//
// Convention:
//   - reads use useQuery + apiGet
//   - writes use useMutation + apiPost / apiPatch
//   - mutation onSuccess invalidates the relevant read keys

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from './api';
import type { ApiError } from './api';
import type { ReplyIntent, EscalationReason } from '@stax/shared';

// ---------- Types (mirrors of the bridge function row shapes) --------------

export interface MeResponse {
  user: { id: string; email: string };
  member: {
    id: string;
    email: string;
    display_name: string;
    role: 'admin' | 'operator' | 'viewer';
    active: boolean;
  };
}

export interface InboxRow {
  email_id: string;
  received_at: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  body_preview: string;
  thread_id: string;
  creator_id: string | null;
  creator_name: string | null;
  intent: ReplyIntent | null;
  confidence: number | null;
  escalation_triggered: boolean | null;
  escalation_reasons: string[] | null;
  draft_id: string | null;
  draft_status: string | null;
  has_open_escalation: boolean;
}

export interface ReplyDetailEmail {
  id: string;
  creator_id: string | null;
  in_reply_to_email_id: string | null;
  gmail_message_id: string;
  gmail_thread_id: string;
  from_email: string;
  from_name: string | null;
  to_email: string;
  subject: string;
  body_text: string | null;
  body_text_full: string | null;
  body_html: string | null;
  received_at: string;
  processed_at: string | null;
  created_at: string;
}

export interface ReplyDetailClassification {
  id: string;
  email_received_id: string;
  intent: ReplyIntent;
  confidence: number;
  extracted_date: string | null;
  extracted_meeting_time: string | null;
  mentions_money: boolean;
  mentions_contract: boolean;
  mentions_pedro: boolean;
  escalation_triggered: boolean;
  escalation_reasons: EscalationReason[] | null;
  reasoning: string | null;
  model_used: string | null;
  raw_response: unknown;
  created_at: string;
}

export interface ReplyDetailDraft {
  id: string;
  email_received_id: string;
  classification_id: string | null;
  template_id: string | null;
  sender_identity_id: string;
  subject: string;
  body_text: string;
  body_html: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'sent';
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  created_at: string;
}

export interface ReplyDetailEscalation {
  id: string;
  email_received_id: string;
  classification_id: string | null;
  draft_id: string | null;
  reason: string;
  severity: 'warn' | 'urgent';
  status: 'open' | 'in_progress' | 'closed';
  notes: string | null;
  created_at: string;
}

export interface ReplyDetailThreadEntry {
  kind: 'inbound';
  id: string;
  subject: string;
  from_email: string;
  received_at: string;
  body_text: string | null;
}

export interface ReplyDetailCreator {
  id: string;
  canonical_name: string;
  email: string;
  platform: string | null;
  handle: string | null;
  // Full creator context for the "who is this?" sidebar on first replies.
  profile_url: string | null;
  subscriber_count: number | null;
  tier: string | null;
  status: string | null;
  first_contacted_at: string | null;
  last_contacted_at: string | null;
  notes: string | null;
  // Pulled from ass.tracker via the bridge — niche + the video that drove
  // the original personalization clause, when we have it.
  primary_niche: string | null;
  video_post_referenced: string | null;
  // Reply history for this creator (across all threads): when did we first
  // hear back, and how many replies in total? Use this to mark "first reply"
  // vs ongoing conversation.
  first_reply_received_at: string | null;
  reply_count: number;
}

export interface ReplyDetailResponse {
  email: ReplyDetailEmail;
  classification: ReplyDetailClassification | null;
  creator: ReplyDetailCreator | null;
  draft: ReplyDetailDraft | null;
  escalations: ReplyDetailEscalation[];
  thread: ReplyDetailThreadEntry[];
}

export interface SenderIdentity {
  id: string;
  display_name: string;
  from_email: string;
  reply_to: string | null;
  signature_html: string | null;
  provider: 'gmail' | 'resend' | string;
  use_for: 'cold' | 'reply' | 'both' | string;
  is_default: boolean;
  active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ResponseTemplate {
  id: string;
  name: string;
  intent: ReplyIntent;
  subject_template: string | null;
  body_template: string;
  variables: unknown;
  sender_identity_id: string | null;
  active: boolean;
  is_default_for_intent: boolean;
  created_at: string;
  updated_at: string;
}

export interface OpenEscalation {
  id: string;
  email_received_id: string;
  classification_id: string | null;
  draft_id: string | null;
  reason: string;
  severity: 'warn' | 'urgent';
  status: 'open' | 'in_progress';
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
}

export interface AppConfigEntry {
  key: string;
  value: unknown;
  description: string | null;
  updated_at: string;
}

export interface CreatorRow {
  id: string;
  canonical_name: string;
  email: string;
  platform: string | null;
  handle: string | null;
  profile_url: string | null;
  subscriber_count: number | null;
  tier: string | null;
  status: string;
  first_contacted_at: string | null;
  last_contacted_at: string | null;
  has_replied: boolean;
  reply_count: number;
  total_count: number;
}

export interface EngagementStageStats {
  sent_count: number;
  recipients: string[];
  last_sent_at: string | null;
}

export interface EngagementStageStatsLive {
  sent_count: number;
  failed_count: number;
  drymeasured: number;
  unique_clicks: number;
  total_clicks: number;
  last_sent_at: string | null;
}

export interface EngagementStageStatsCombined extends EngagementStageStats {
  ass_sent_count?: number;
  ass_failed_count?: number;
  ass_dry_count?: number;
  ass_unique_clicks?: number;
  ass_total_clicks?: number;
  ass_last_sent_at?: string | null;
}

export interface EngagementResponse {
  analytics: Record<string, EngagementStageStatsCombined>;
  clicks_status: 'not_wired' | 'wired';
  clicks_note: string;
}

export interface EngagementRecentSend {
  id: string;
  recipient_email: string;
  subject: string | null;
  status: string;
  resend_message_id: string | null;
  error: string | null;
  sent_at: string | null;
  created_at: string;
  click_count: number;
}

// Same as EngagementRecentSend plus stage_id — used by the cross-stage
// activity feed so a single list can show emails from every stage with
// the stage label visible per row.
export interface EngagementRecentSendAll extends EngagementRecentSend {
  stage_id: string;
}

// Paginated response from /api/engagement/recent-all. Carries total so the
// UI can render "1–50 of N" and disable the Next button on the last page.
export interface EngagementRecentAllPage {
  rows: EngagementRecentSendAll[];
  total: number;
  limit: number;
  offset: number;
}

// Meeting-invite tracking — replies that contain a calendar invite.
// Attribution: total_attributed counts only invites that were replies to a
// Stax-sent email (in_reply_to_email_id IS NOT NULL); total_all includes
// any inbound meeting invite (e.g., a calendar invite from Luca's spouse).
// Dashboard displays the attributed metric prominently.
export interface MeetingInvitesStats {
  total_attributed: number;
  last_7d_attributed: number;
  total_all: number;
  last_7d_all: number;
  last_received_at: string | null;
}

export interface MeetingInviteRow {
  id: string;
  received_at: string;
  from_email: string;
  from_name: string | null;
  subject: string | null;
  creator_id: string | null;
  creator_name: string | null;
  in_reply_to_email_id: string | null;
  thread_id: string | null;
}

// Podcast episodes — weekly Stax-themed investing show. Research generated
// Sundays 14:00 UTC by the worker; Luca takes the markdown → NotebookLM →
// audio → Spotify, then pastes the URLs back. See ass.podcast_episodes.
export type PodcastEpisodeStatus =
  | 'researching'
  | 'ready_for_audio'
  | 'audio_recorded'
  | 'published'
  | 'failed';

export interface PodcastEpisodeRow {
  id: string;
  episode_number: number;
  week_of: string;
  status: PodcastEpisodeStatus;
  topic: string | null;
  angle: string | null;
  stax_connection: string | null;
  source_urls: string[];
  notebooklm_url: string | null;
  spotify_url: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

// Per-episode Spotify description — auto-written by the research engine
// (LLM, enforcing no em dash / no if-then). Lives on the detail object.

// Full detail — includes the research markdown for rendering in the
// episode detail view.
export interface PodcastEpisodeDetail extends PodcastEpisodeRow {
  research_markdown: string | null;
  topic_score: number | null;
  picker_metadata: Record<string, unknown>;
  notes: string | null;
  // Instructions for NotebookLM's Audio Overview "Customize" box. Controls
  // the AI hosts' tone, banned words, casualness, etc. Paste into NotebookLM
  // at audio-generation time. Has a sensible default; editable per-episode.
  notebooklm_customization_prompt: string;
  // Spotify episode description — paste under the episode on Spotify.
  // Editable per-episode.
  spotify_description: string | null;
}

// Interview debriefs — generated by the stax-interview-debrief skill in Claude
// Code, then pushed to ass.interview_debriefs. The dashboard reads them through
// ass_interview_*. See app/apps/worker/sql/interview_debriefs.sql.
export type InterviewType = 'partnership' | 'user';

// Lightweight list row (no heavy markdown). All derived fields come from the
// full record.
export interface InterviewListRow {
  interview_id: string;
  interview_type: InterviewType;
  date: string | null;
  title: string | null;
  coverage_confidence: string | null;
  decision: string | null;     // partnership
  disposition: string | null;  // user
  familiarity: string | null;  // user
  counterpart: string | null;  // company name or user id
  created_at: string;
  updated_at: string;
}

// The structured interview.json — loosely typed; the detail view renders known
// fields and falls back gracefully. Schema: skill reference/analytics.md.
export type InterviewRecord = Record<string, unknown>;

export interface InterviewDetail {
  interview_id: string;
  interview_type: InterviewType;
  date: string | null;
  title: string | null;
  coverage_confidence: string | null;
  decision: string | null;
  disposition: string | null;
  familiarity: string | null;
  counterpart: string | null;
  report_md: string | null;
  one_pager_md: string | null;
  record: InterviewRecord;
  raw_sources: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

type SentimentBreakdown = { positive: number; neutral: number; negative: number };

export interface InterviewAggregates {
  schema_version: string;
  updated: string | null;
  totals: { interviews: number; partnership: number; user: number };
  partnership: {
    decisions: Record<string, number>;
    compatibility_structure: Record<string, number>;
  };
  user: {
    familiarity: Record<string, number>;
    dispositions: Record<string, number>;
    topics: Record<string, number>;
    sentiment: Record<string, number>;
    response_types: Record<string, number>;
    questions: Record<string, { text: string; topic: string; times_asked: number; first_seen: string | null; last_seen: string | null }>;
    response_categories: Record<string, { text: string; count: number; sentiment_breakdown: SentimentBreakdown }>;
  };
}

export interface InterviewQuestionCatalogEntry {
  id: string;
  text: string;
  topic: string;
  aliases: string[];
  times_asked: number;
  interviews: number;
  first_seen: string | null;
  last_seen: string | null;
  seeded: boolean;
}

export interface InterviewResponseCatalogEntry {
  id: string;
  text: string;
  type: string;
  count: number;
  sentiment_breakdown: SentimentBreakdown;
  first_seen: string | null;
  last_seen: string | null;
}

export interface InterviewAnalyticsResponse {
  aggregates: InterviewAggregates;
  question_catalog: { schema_version: string; questions: InterviewQuestionCatalogEntry[] };
  response_catalog: { schema_version: string; responses: InterviewResponseCatalogEntry[] };
}

// ---- Pain points (Leadership Touchpoint #3) -------------------------------
// Weekly user/team friction, managed from the dashboard. Bridge functions
// ass_painpoint_*. See app/apps/worker/sql/pain_points.sql.
export type PainType = 'new' | 'recurring';
export type PainStatus = 'open' | 'in_progress' | 'resolved' | 'recurring';
export type PainAudience = 'user' | 'team';
export type PainSeverity = 'high' | 'medium' | 'low';

export interface PainPointRow {
  pain_id: string;
  week_of: string | null;
  title: string;
  detail: string | null;
  source: string | null;
  audience: PainAudience | null;
  pain_type: PainType;
  severity: PainSeverity | null;
  surface: string | null;
  status: PainStatus;
  owner: string | null;
  recurrence_count: number;
  root_cause: string | null;
  tasx_step_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PainPointDetail extends PainPointRow {
  record: Record<string, unknown>;
}

// The editable shape the upsert accepts under { record: ... }.
export interface PainPointInput {
  pain_id: string;
  week_of: string | null;
  title: string;
  detail?: string | null;
  source?: string | null;
  audience?: PainAudience | null;
  pain_type: PainType;
  severity?: PainSeverity | null;
  surface?: string | null;
  status?: PainStatus;
  owner?: string | null;
  recurrence_count?: number;
  root_cause?: string | null;
}

export interface PainPointWeek {
  week_of: string;
  total: number;
  new: number;
  recurring: number;
  resolved: number;
}

export interface PainPointRecurringOpen {
  pain_id: string;
  title: string;
  surface: string | null;
  recurrence_count: number;
  weeks_open: number | null;
  root_cause: string | null;
}

export interface PainPointAnalytics {
  updated: string | null;
  totals: { total: number; open: number; in_progress: number; resolved: number; recurring: number };
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  by_audience: Record<string, number>;
  by_surface: Record<string, number>;
  by_week: PainPointWeek[];
  recurring_open: PainPointRecurringOpen[];
  resolution_rate: number;
}

// ---- User stories (Leadership Touchpoint #1 — "User stories submitted") ----
// Drafted + reviewed on the dashboard, then sent to tasx as a checklist step.
// Bridge functions ass_userstory_*. See app/apps/worker/sql/user_stories.sql.
export type UserStoryStatus = 'draft' | 'submitted' | 'accepted';
export type StorySeverity = 'high' | 'medium' | 'low';

export interface UserStoryRow {
  story_id: string;
  week_of: string | null;
  title: string | null;
  as_a: string | null;
  i_want: string;
  so_that: string | null;
  acceptance: string | null;
  priority: StorySeverity | null;
  source: string | null;
  source_interview_id: string | null;
  status: UserStoryStatus;
  tasx_step_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserStoryDetail extends UserStoryRow {
  record: Record<string, unknown>;
}

export interface UserStoryInput {
  story_id: string;
  week_of: string | null;
  title?: string | null;
  as_a?: string | null;
  i_want: string;
  so_that?: string | null;
  acceptance?: string | null;
  priority?: StorySeverity | null;
  source?: string | null;
  source_interview_id?: string | null;
  status?: UserStoryStatus;
}

// Paginated response from /api/meetings/recent. Same shape as the engagement
// activity feed so the UI pager can be a near-clone.
export interface MeetingInvitesPage {
  rows: MeetingInviteRow[];
  total: number;
  limit: number;
  offset: number;
  attributed_only: boolean;
}

export interface DisengagementStats {
  cohort_count: number;
  median_days: number;
  p90_days: number;
  oldest_days: number;
  bucket_0_7: number;
  bucket_8_14: number;
  bucket_15_30: number;
  bucket_31_90: number;
  bucket_91_plus: number;
}

export interface EngagementSweepResult {
  skipped?: boolean;
  reason?: string;
  config: { send_enabled: boolean; dry_run: boolean; max_per_run: number };
  stages: Record<string, {
    eligible: number;
    attempted: number;
    sent: number;
    dry: number;
    failed: number;
    errors: string[];
  }>;
}

export interface SystemHealth {
  snapshot_at: string;
  emails_received_24h: number;
  emails_received_7d: number;
  emails_received_total: number;
  emails_sent_24h: number;
  emails_sent_7d: number;
  emails_sent_total: number;
  classifications_24h: number;
  classifications_total: number;
  escalations_open: number;
  drafts_pending: number;
  engagement_sends_24h: number;
  engagement_sends_7d: number;
  engagement_dry_24h: number;
  engagement_failed_24h: number;
  engagement_events_24h: number;
  stax_users_total: number;
  stax_users_active_30d: number;
  stax_users_disengaged_30d: number;
  stax_users_never_logged_in: number;
  stax_users_signups_24h: number;
  stax_users_signups_7d: number;
  stax_paid_subscriptions: number;
  creators_total: number;
  creators_replied: number;
  team_members: number;
  last_email_received_at: string | null;
  last_email_sent_at: string | null;
  last_engagement_send_at: string | null;
}

export interface CreatorFilters {
  search?: string;
  platform?: string;
  date_from?: string;
  date_to?: string;
  min_subs?: string;
  max_subs?: string;
  replied?: 'true' | 'false' | '';
  limit?: number;
  offset?: number;
}

// ---------- Query keys -----------------------------------------------------

export const qk = {
  me: () => ['me'] as const,
  inbox: () => ['inbox'] as const,
  reply: (id: string) => ['replies', id] as const,
  senders: () => ['sender-identities'] as const,
  templates: () => ['templates'] as const,
  escalations: () => ['escalations'] as const,
  config: () => ['config'] as const,
  creators: (filters: CreatorFilters) => ['creators', filters] as const,
  engagement: () => ['engagement'] as const,
  engagementStats: (stageId: string) => ['engagement', 'stats', stageId] as const,
  engagementRecent: (stageId: string) => ['engagement', 'recent', stageId] as const,
  engagementRecentAll: (offset: number, limit: number) =>
    ['engagement', 'recent-all', offset, limit] as const,
  meetingsStats: () => ['meetings', 'stats'] as const,
  meetingsRecent: (offset: number, limit: number, attributedOnly: boolean) =>
    ['meetings', 'recent', offset, limit, attributedOnly] as const,
  podcastEpisodes: () => ['podcast', 'episodes'] as const,
  podcastEpisode: (id: string) => ['podcast', 'episode', id] as const,
  interviews: () => ['interviews'] as const,
  interview: (id: string) => ['interviews', 'detail', id] as const,
  interviewAnalytics: () => ['interviews', 'analytics'] as const,
  painPoints: () => ['pain-points'] as const,
  painPoint: (id: string) => ['pain-points', 'detail', id] as const,
  painPointAnalytics: () => ['pain-points', 'analytics'] as const,
  userStories: () => ['user-stories'] as const,
  userStory: (id: string) => ['user-stories', 'detail', id] as const,
  engagementDisengagement: (stageId: string) => ['engagement', 'disengagement', stageId] as const,
  systemHealth: () => ['system', 'health'] as const,
};

// ---------- Read hooks -----------------------------------------------------

export function useMe(opts?: Omit<UseQueryOptions<MeResponse, ApiError>, 'queryKey' | 'queryFn'>) {
  return useQuery<MeResponse, ApiError>({
    queryKey: qk.me(),
    queryFn: () => apiGet<MeResponse>('/api/me'),
    ...opts,
  });
}

export function useInbox() {
  return useQuery<InboxRow[], ApiError>({
    queryKey: qk.inbox(),
    queryFn: () => apiGet<InboxRow[]>('/api/inbox'),
  });
}

export function useReplyDetail(emailId: string | undefined) {
  return useQuery<ReplyDetailResponse, ApiError>({
    queryKey: qk.reply(emailId ?? ''),
    queryFn: () => apiGet<ReplyDetailResponse>(`/api/replies/${emailId}`),
    enabled: Boolean(emailId),
  });
}

export function useSenderIdentities() {
  return useQuery<SenderIdentity[], ApiError>({
    queryKey: qk.senders(),
    queryFn: () => apiGet<SenderIdentity[]>('/api/sender-identities'),
  });
}

export function useTemplates() {
  return useQuery<ResponseTemplate[], ApiError>({
    queryKey: qk.templates(),
    queryFn: () => apiGet<ResponseTemplate[]>('/api/templates'),
  });
}

export function useEscalations() {
  return useQuery<OpenEscalation[], ApiError>({
    queryKey: qk.escalations(),
    queryFn: () => apiGet<OpenEscalation[]>('/api/escalations'),
  });
}

export function useConfig() {
  return useQuery<AppConfigEntry[], ApiError>({
    queryKey: qk.config(),
    queryFn: () => apiGet<AppConfigEntry[]>('/api/config'),
  });
}

export function useEngagement() {
  return useQuery<EngagementResponse, ApiError>({
    queryKey: qk.engagement(),
    queryFn: () => apiGet<EngagementResponse>('/api/engagement'),
  });
}

export function useEngagementRecent(stageId: string, limit = 20) {
  return useQuery<EngagementRecentSend[], ApiError>({
    queryKey: qk.engagementRecent(stageId),
    queryFn: () => apiGet<EngagementRecentSend[]>(`/api/engagement/recent/${stageId}?limit=${limit}`),
    enabled: Boolean(stageId),
  });
}

export function useMeetingsStats() {
  return useQuery<MeetingInvitesStats, ApiError>({
    queryKey: qk.meetingsStats(),
    queryFn: () => apiGet<MeetingInvitesStats>('/api/meetings/stats'),
    refetchInterval: 60_000,
  });
}

export function useMeetingsRecent(offset = 0, limit = 50, attributedOnly = false) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (attributedOnly) params.set('attributed', '1');
  return useQuery<MeetingInvitesPage, ApiError>({
    queryKey: qk.meetingsRecent(offset, limit, attributedOnly),
    queryFn: () => apiGet<MeetingInvitesPage>(`/api/meetings/recent?${params.toString()}`),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function usePodcastEpisodes() {
  return useQuery<PodcastEpisodeRow[], ApiError>({
    queryKey: qk.podcastEpisodes(),
    queryFn: () => apiGet<PodcastEpisodeRow[]>('/api/podcast/episodes?limit=100'),
    refetchInterval: 60_000,
  });
}

export function usePodcastEpisode(id: string | undefined) {
  return useQuery<PodcastEpisodeDetail, ApiError>({
    queryKey: qk.podcastEpisode(id ?? ''),
    queryFn: () => apiGet<PodcastEpisodeDetail>(`/api/podcast/episodes/${id}`),
    enabled: Boolean(id),
  });
}

export function useUpdatePodcastEpisode(id: string) {
  const qc = useQueryClient();
  return useMutation<
    PodcastEpisodeDetail,
    ApiError,
    Partial<
      Pick<
        PodcastEpisodeDetail,
        | 'status'
        | 'notebooklm_url'
        | 'spotify_url'
        | 'notes'
        | 'notebooklm_customization_prompt'
        | 'spotify_description'
      >
    >
  >({
    mutationFn: (patch) => apiPatch<PodcastEpisodeDetail>(`/api/podcast/episodes/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.podcastEpisode(id) });
      qc.invalidateQueries({ queryKey: qk.podcastEpisodes() });
    },
  });
}

export function useInterviews() {
  return useQuery<InterviewListRow[], ApiError>({
    queryKey: qk.interviews(),
    queryFn: () => apiGet<InterviewListRow[]>('/api/interviews?limit=200'),
    refetchInterval: 60_000,
  });
}

export function useInterview(id: string | undefined) {
  return useQuery<InterviewDetail, ApiError>({
    queryKey: qk.interview(id ?? ''),
    queryFn: () => apiGet<InterviewDetail>(`/api/interviews/${id}`),
    enabled: Boolean(id),
  });
}

export function useInterviewAnalytics() {
  return useQuery<InterviewAnalyticsResponse, ApiError>({
    queryKey: qk.interviewAnalytics(),
    queryFn: () => apiGet<InterviewAnalyticsResponse>('/api/interviews/analytics'),
    refetchInterval: 60_000,
  });
}

// ---- Pain points ----------------------------------------------------------

export function usePainPoints() {
  return useQuery<PainPointRow[], ApiError>({
    queryKey: qk.painPoints(),
    queryFn: () => apiGet<PainPointRow[]>('/api/pain-points?limit=500'),
    refetchInterval: 60_000,
  });
}

export function usePainPointAnalytics() {
  return useQuery<PainPointAnalytics, ApiError>({
    queryKey: qk.painPointAnalytics(),
    queryFn: () => apiGet<PainPointAnalytics>('/api/pain-points/analytics'),
    refetchInterval: 60_000,
  });
}

export interface PainPointUpsertResult {
  ok: boolean;
  pain_point: PainPointDetail | null;
  tasx_step_id?: string | null;
  tasx_error?: string;
}

export function useUpsertPainPoint() {
  const qc = useQueryClient();
  return useMutation<PainPointUpsertResult, ApiError, PainPointInput>({
    mutationFn: (record) =>
      apiPost<PainPointUpsertResult>('/api/pain-points/upsert', { record }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.painPoints() });
      qc.invalidateQueries({ queryKey: qk.painPointAnalytics() });
    },
  });
}

// ---- User stories ---------------------------------------------------------

export function useUserStories() {
  return useQuery<UserStoryRow[], ApiError>({
    queryKey: qk.userStories(),
    queryFn: () => apiGet<UserStoryRow[]>('/api/user-stories?limit=500'),
    refetchInterval: 60_000,
  });
}

export function useUpsertUserStory() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; user_story: UserStoryDetail | null }, ApiError, UserStoryInput>({
    mutationFn: (record) =>
      apiPost<{ ok: boolean; user_story: UserStoryDetail | null }>('/api/user-stories/upsert', { record }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.userStories() }),
  });
}

export function useSendUserStory() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; tasx_step_id: string | null }, ApiError, string>({
    mutationFn: (storyId) =>
      apiPost<{ ok: boolean; tasx_step_id: string | null }>(`/api/user-stories/${storyId}/send`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.userStories() }),
  });
}

// Generate draft stories from a user interview (the dashboard writes them).
export function useGenerateUserStories() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; generated: number }, ApiError, string>({
    mutationFn: (interviewId) =>
      apiPost<{ ok: boolean; generated: number }>(`/api/user-stories/generate-from-interview/${interviewId}`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.userStories() }),
  });
}

export function useEngagementRecentAll(offset = 0, limit = 50) {
  return useQuery<EngagementRecentAllPage, ApiError>({
    queryKey: qk.engagementRecentAll(offset, limit),
    queryFn: () =>
      apiGet<EngagementRecentAllPage>(
        `/api/engagement/recent-all?limit=${limit}&offset=${offset}`,
      ),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useDisengagement(stageId: string) {
  return useQuery<DisengagementStats | null, ApiError>({
    queryKey: qk.engagementDisengagement(stageId),
    queryFn: () => apiGet<DisengagementStats | null>(`/api/engagement/disengagement/${stageId}`),
    enabled: Boolean(stageId),
  });
}

export function useSystemHealth() {
  return useQuery<SystemHealth, ApiError>({
    queryKey: qk.systemHealth(),
    queryFn: () => apiGet<SystemHealth>('/api/system/health'),
    refetchInterval: 30_000,
  });
}

export function useRunEngagementSweep() {
  const qc = useQueryClient();
  return useMutation<EngagementSweepResult, ApiError, void>({
    mutationFn: () => apiPost<EngagementSweepResult>('/api/engagement/run', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.engagement() });
      qc.invalidateQueries({ queryKey: ['engagement', 'stats'] });
      qc.invalidateQueries({ queryKey: ['engagement', 'recent'] });
      qc.invalidateQueries({ queryKey: qk.systemHealth() });
    },
  });
}

export function useCreators(filters: CreatorFilters = {}) {
  return useQuery<CreatorRow[], ApiError>({
    queryKey: qk.creators(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
      }
      const qs = params.toString();
      return apiGet<CreatorRow[]>(`/api/creators${qs ? '?' + qs : ''}`);
    },
    placeholderData: (prev) => prev,
  });
}

// ---------- Mutation hooks -------------------------------------------------

export function useAIDraft() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; draft_id: string; subject: string; body_text: string; model_used: string },
    ApiError,
    { emailId: string }
  >({
    mutationFn: ({ emailId }) => apiPost(`/api/replies/${emailId}/ai-draft`, {}),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.reply(vars.emailId) });
    },
  });
}

export function useSendReply() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; sent_message_id: string; sent_email_id?: string },
    ApiError,
    { emailId: string; subject: string; body_text: string; draft_id?: string }
  >({
    mutationFn: ({ emailId, subject, body_text, draft_id }) =>
      apiPost(`/api/replies/${emailId}/send`, { subject, body_text, draft_id }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.inbox() });
      qc.invalidateQueries({ queryKey: qk.reply(vars.emailId) });
      qc.invalidateQueries({ queryKey: qk.systemHealth() });
    },
  });
}

export function useApproveDraft(
  opts?: Omit<UseMutationOptions<{ ok: true }, ApiError, { draftId: string; emailId: string }>, 'mutationFn'>,
) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { draftId: string; emailId: string }>({
    mutationFn: ({ draftId }) => apiPost<{ ok: true }>(`/api/drafts/${draftId}/approve`, {}),
    onSuccess: (...args) => {
      const vars = args[1];
      qc.invalidateQueries({ queryKey: qk.inbox() });
      qc.invalidateQueries({ queryKey: qk.reply(vars.emailId) });
      qc.invalidateQueries({ queryKey: qk.escalations() });
      opts?.onSuccess?.(...args);
    },
    ...opts,
  });
}

export function useRejectDraft(
  opts?: Omit<
    UseMutationOptions<
      { ok: true },
      ApiError,
      { draftId: string; emailId: string; reason: string }
    >,
    'mutationFn'
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    { ok: true },
    ApiError,
    { draftId: string; emailId: string; reason: string }
  >({
    mutationFn: ({ draftId, reason }) =>
      apiPost<{ ok: true }>(`/api/drafts/${draftId}/reject`, { reason }),
    onSuccess: (...args) => {
      const vars = args[1];
      qc.invalidateQueries({ queryKey: qk.inbox() });
      qc.invalidateQueries({ queryKey: qk.reply(vars.emailId) });
      qc.invalidateQueries({ queryKey: qk.escalations() });
      opts?.onSuccess?.(...args);
    },
    ...opts,
  });
}

export function useSetConfig(
  opts?: Omit<
    UseMutationOptions<{ ok: true }, ApiError, { key: string; value: unknown }>,
    'mutationFn'
  >,
) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { key: string; value: unknown }>({
    mutationFn: ({ key, value }) => apiPatch<{ ok: true }>(`/api/config/${key}`, { value }),
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: qk.config() });
      opts?.onSuccess?.(...args);
    },
    ...opts,
  });
}
